import { useState } from 'react'
import { ArrowLeft, Eye, EyeOff, Loader2 } from 'lucide-react'

interface AuthScreenProps {
  onBack: () => void
  onSignIn: (email: string, password: string) => Promise<void>
  onSignUp: (email: string, password: string) => Promise<void>
  onGoogleSignIn: () => Promise<void>
  theme: 'light' | 'dark'
  initialError?: string | null
}

export function AuthScreen({ onBack, onSignIn, onSignUp, onGoogleSignIn, theme, initialError = null }: AuthScreenProps) {
  const [isSignUp, setIsSignUp] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [error, setError] = useState<string | null>(initialError)
  const [isLoading, setIsLoading] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!email.trim() || !password.trim()) {
      setError('Please fill in all fields.')
      return
    }

    if (isSignUp && password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters.')
      return
    }

    setIsLoading(true)
    try {
      if (isSignUp) {
        await onSignUp(email.trim(), password)
      } else {
        await onSignIn(email.trim(), password)
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong. Please try again.'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const toggleMode = () => {
    setIsSignUp(prev => !prev)
    setError(null)
    setConfirmPassword('')
  }

  return (
    <div
      className="h-screen flex flex-col"
      style={{ backgroundColor: 'hsl(var(--color-bg-primary))' }}
    >
      {/* Back button */}
      <div className="px-5 py-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm font-medium transition-fast hover:opacity-80"
          style={{ color: 'hsl(var(--color-text-secondary))' }}
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>
      </div>

      {/* Centered form */}
      <div className="flex-1 flex items-center justify-center px-5">
        <div className="w-full max-w-sm">
          {/* Logo mark */}
          <div className="flex justify-center mb-6">
            <img
              src={theme === 'dark' ? '/logo-white.png' : '/logo-black.png'}
              alt="Jot Logo"
              className="w-20 h-20 object-contain"
            />
          </div>

          {/* Heading */}
          <h1
            className="text-2xl font-bold text-center mb-1"
            style={{ color: 'hsl(var(--color-text-primary))' }}
          >
            {isSignUp ? 'Create your account' : 'Welcome back'}
          </h1>
          <p
            className="text-sm text-center mb-8"
            style={{ color: 'hsl(var(--color-text-secondary))' }}
          >
            {isSignUp
              ? 'Start syncing your notes across devices'
              : 'Sign in to sync your notes'}
          </p>

          {/* Google sign-in */}
          <button
            type="button"
            onClick={async () => {
              setError(null)
              setIsGoogleLoading(true)
              try {
                await onGoogleSignIn()
              } catch (err) {
                const message = err instanceof Error ? err.message : 'Google sign-in failed. Please try again.'
                setError(message)
                setIsGoogleLoading(false)
              }
            }}
            disabled={isLoading || isGoogleLoading}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-fast hover:opacity-90 disabled:opacity-60"
            style={{
              backgroundColor: 'hsl(var(--color-bg-secondary))',
              color: 'hsl(var(--color-text-primary))',
              border: '1px solid hsl(var(--color-border))',
            }}
          >
            {isGoogleLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <svg className="w-4 h-4" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
              </svg>
            )}
            {isGoogleLoading ? 'Redirecting...' : 'Continue with Google'}
          </button>

          {/* Divider */}
          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px" style={{ backgroundColor: 'hsl(var(--color-border))' }} />
            <span
              className="text-xs font-medium"
              style={{ color: 'hsl(var(--color-text-muted))' }}
            >
              or
            </span>
            <div className="flex-1 h-px" style={{ backgroundColor: 'hsl(var(--color-border))' }} />
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Email */}
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="Email address"
              autoComplete="email"
              disabled={isLoading}
              className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-fast placeholder:opacity-50"
              style={{
                backgroundColor: 'hsl(var(--color-bg-secondary))',
                color: 'hsl(var(--color-text-primary))',
                border: '1px solid hsl(var(--color-border))',
              }}
            />

            {/* Password */}
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Password"
                autoComplete={isSignUp ? 'new-password' : 'current-password'}
                disabled={isLoading}
                className="w-full px-4 py-3 pr-11 rounded-xl text-sm outline-none transition-fast placeholder:opacity-50"
                style={{
                  backgroundColor: 'hsl(var(--color-bg-secondary))',
                  color: 'hsl(var(--color-text-primary))',
                  border: '1px solid hsl(var(--color-border))',
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(prev => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded transition-fast hover:opacity-80"
                style={{ color: 'hsl(var(--color-text-muted))' }}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>

            {/* Confirm Password (sign-up only) */}
            {isSignUp && (
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Confirm password"
                  autoComplete="new-password"
                  disabled={isLoading}
                  className="w-full px-4 py-3 pr-11 rounded-xl text-sm outline-none transition-fast placeholder:opacity-50"
                  style={{
                    backgroundColor: 'hsl(var(--color-bg-secondary))',
                    color: 'hsl(var(--color-text-primary))',
                    border: '1px solid hsl(var(--color-border))',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(prev => !prev)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded transition-fast hover:opacity-80"
                  style={{ color: 'hsl(var(--color-text-muted))' }}
                  tabIndex={-1}
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            )}

            {/* Error message */}
            {error && (
              <p
                className="text-sm px-1"
                style={{ color: 'hsl(var(--color-error))' }}
              >
                {error}
              </p>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading || isGoogleLoading}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-fast hover:opacity-90 disabled:opacity-60"
              style={{
                backgroundColor: 'hsl(var(--color-accent))',
                color: 'hsl(var(--color-bg-primary))',
              }}
            >
              {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
              {isSignUp ? 'Create Account' : 'Sign In'}
            </button>
          </form>

          {/* Toggle link */}
          <p
            className="text-sm text-center mt-6"
            style={{ color: 'hsl(var(--color-text-secondary))' }}
          >
            {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
            <button
              onClick={toggleMode}
              className="font-medium hover:opacity-80 transition-fast"
              style={{ color: 'hsl(var(--color-accent))' }}
            >
              {isSignUp ? 'Sign in' : 'Sign up'}
            </button>
          </p>
        </div>
      </div>
    </div>
  )
}
